import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthUser } from '../../contexts/AuthProvider/hooks';
import { useConfig } from '../../eliot-components/src/ConfigProvider/hooks';
import Layout from '../../Layout/Layout';
import {
  shouldShowSignalStrength,
  shouldShowBatteryVoltage,
  shouldShowSyncData,
  chartColor,
  shouldShowAvgSignalStrength,
  shouldShowAvgBatteryVoltage,
} from './provider';
import CountUp from 'react-countup';
import SignalStrengthPieChart from './SignalStrengthPieChart/SignalStrengthPieChart';
import SyncDataPieChart from './SyncDataPieChart/SyncDataPieChart';
import BatteryHealthPieChart from './BatteryHealthPieChart/BatteryHealthPieChart';
import AlertsBarChart from './AlertsBarChart/AlertsBarChart';
import DeviceHub from '@material-ui/icons/DeviceHub';
import { categories } from './constants';
import Filters, {
  lastStatusCodeFilterWrapper,
} from '../Devices/components/Filters/Filters';
import wrapFilters from '../Devices/components/Filters/wrapFilters';
import {
  filtersFromQueryString,
  queryStringFromFilters,
} from '../Devices/components/Filters/queryString';
import { useKnownIssues } from '../Alerts/hooks';
import * as domToImage from 'html-to-image';
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core';
import { saveAs } from 'file-saver';
import { KeyboardArrowDown } from '@material-ui/icons';
import { MedisanteOrgReport } from './types';
import StatusCodePieChart from './StatusCodePieChart/StatusCodePieChart';
import {
  useGetReportsQuery,
  useGetTargetSystemsQuery,
} from '../../store/api-slice';
import { useAppSelector } from '../../hooks';
import {
  calculateIsMaximumDevicesReached,
  isTotalMeasurementCount,
  mergeFilters,
} from '../../utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  statusPanel: {
    padding: theme.spacing(1),
    minHeight: 180,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
      height: 270,
      overflowY: 'auto',
    },
  },

  screenshotButton: {
    margin: theme.spacing(1.5, 1, 1, 1),
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(2, 1, 1, 1),
    },
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1.5, 1, 1, 0),
    },
    'button&': {
      height: 'fit-content',
      backgroundColor: theme.palette.action.hover,
    },

    '& .labelContainer': {
      display: 'inline-flex',
      textAlign: 'left',
      alignItems: 'center',
      '& .label': {
        flexGrow: 1,
        paddingRight: theme.spacing(1),
      },
    },
  },

  noTargetSystemsPanel: {
    padding: theme.spacing(2),
    height: 270,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },

  noTargetSystemIcon: {
    color: chartColor(categories.CRITICAL.value),
  },

  devicesPanel: {
    height: 270,
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',

    '& .deviceCount': {
      textAlign: 'center',
      width: '100%',
      padding: theme.spacing(1),

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },

    '& h2': {
      textAlign: 'center',
      display: 'inline-block',
    },

    [theme.breakpoints.down('xs')]: {
      '& h2': {
        fontSize: theme.typography.h4.fontSize,
      },
    },
  },

  measurementsPanel: {
    height: 270,
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'default',

    '& .measurementRow': {
      textAlign: 'center',
      width: '100%',
      padding: theme.spacing(1),

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },

    [theme.breakpoints.down('xs')]: {
      '& h2': {
        fontSize: theme.typography.h4.fontSize,
      },
    },
  },
  statsStrip: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    padding: theme.spacing(1, 0),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
    '& .statItem': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(1, 2),
      [theme.breakpoints.down('xs')]: {
        flex: 'none',
        padding: theme.spacing(2),
      },
    },
    '& .statItem.clickable': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
  dashboard: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    '& .mainContent': {
      minWidth: '285px',
      maxHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        paddingBottom: theme.spacing(12),
      },
      [theme.breakpoints.up('lg')]: {
        width: 'calc(100% - 300px)',
      },
      '& .dashboardContent': {
        [theme.breakpoints.down('xs')]: {
          '& .MuiGrid-container': {
            margin: 0,
            width: '100%',

            '& .MuiGrid-item': {
              padding: theme.spacing(0, 0, 1, 0),
            },
          },
        },
      },
    },
  },
}));

export const reportsInCSVFormat = (reports: MedisanteOrgReport[]) => {
  const getTotal = (getProp: (r: MedisanteOrgReport) => number) =>
    reports.map(getProp).reduce((prev, next) => prev + next, 0);

  let items =
    'Measurement count,Device Count,Battery Health Critical,Battery Health Warning,Battery Health OK,Sync Activity Critical,Sync Activity OK,Signal Strength Critical,Signal Strength Warning,Signal Strength OK,No webhooks set,Has pending changes\n';
  items += `${getTotal((r) => r.measurementCount)},${getTotal(
    (r) => r.deviceCount
  )},${getTotal((r) => r.batteryCritical)},${getTotal(
    (r) => r.batteryWarning
  )},${getTotal((r) => r.batteryOK)},${getTotal(
    (r) => r.syncScheduleCritical
  )},${getTotal((r) => r.syncScheduleOK)},${getTotal(
    (r) => r.signalStrengthCritical
  )},${getTotal((r) => r.signalStrengthWarning)},${getTotal(
    (r) => r.signalStrengthOK
  )},${getTotal((r) => r.noTargetSystemSet)},${getTotal(
    (r) => r.hasPendingChanges
  )}\n`;

  return items;
};

const Dashboard = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('xs')
  );
  const location = useLocation();
  const history = useHistory();
  const config = useConfig();
  const { authUser } = useAuthUser();
  const mainContentRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLButtonElement>(null);
  const [isExportMenuOpened, setIsExportMenuOpened] = useState<boolean>(false);
  const reduxFilters = useAppSelector((state) => state.appState.deviceFilters);
  // read filters from redux or from url (only on refresh or when is not in redux yet)
  // filters are set to redux inside Filters component
  const filters = useMemo(() => {
    let queryFilters = filtersFromQueryString(location.search);
    return wrapFilters(mergeFilters(reduxFilters, queryFilters));
  }, [location.search, reduxFilters]);
  useEffect(() => {
    try {
      const search = queryStringFromFilters(filters);
      if (history.location.search !== search) {
        history.replace({ search });
      }
    } catch (e) {
      console.warn('error parsing filters: ' + e);
    }
  }, [history, filters]);
  const globalSearch = filters.find(
    (d) => d.category === 'serial_number_or_imei'
  );

  const { isLoading: isLoadingTs, data: targetSystems = [] } =
    useGetTargetSystemsQuery({
      apiBaseURL: config.apiBaseURL,
      filters: [],
      orgs: !!globalSearch ? [] : authUser.selectedOrgs,
    });

  const dashboardFilters = useMemo(
    () => lastStatusCodeFilterWrapper(filters, targetSystems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, targetSystems]
  );

  const {
    data: reports = [],
    error,
    isFetching: isLoadingReports,
  } = useGetReportsQuery({
    apiBaseURL: config.apiBaseURL,
    filters: dashboardFilters,
    orgs: !!globalSearch ? [] : authUser.selectedOrgs,
  });

  const { knownIssues } = useKnownIssues(reports);

  const showSyncData = useMemo(() => shouldShowSyncData(reports), [reports]);

  const showBatteryVoltage = useMemo(
    () => shouldShowBatteryVoltage(reports),
    [reports]
  );
  const showBatteryAvgVoltage = useMemo(
    () => shouldShowAvgBatteryVoltage(reports),
    [reports]
  );

  const showSignalStrength = useMemo(
    () => shouldShowSignalStrength(reports),
    [reports]
  );

  const showSignalAvgStrength = useMemo(
    () => shouldShowAvgSignalStrength(reports),
    [reports]
  );

  const showStatusCodes = useMemo(
    () => targetSystems.filter((ts) => !!ts.lastStatusCode).length > 0,
    [targetSystems]
  );

  const mediumScreenWidth = 6;
  const isMaximumDevicesReached = calculateIsMaximumDevicesReached(
    authUser,
    reports
  );
  const devicesCount = useMemo(
    () => reports.reduce((agg, report) => agg + (report.deviceCount || 0), 0),
    [reports]
  );
  const measurementRealView = isTotalMeasurementCount();
  const measurementCount = useMemo(
    () =>
      reports.reduce(
        (agg, report) =>
          agg +
          (measurementRealView
            ? report.measurementCount
            : report.measurementTripCount),
        0
      ),
    [reports, measurementRealView]
  );
  // const orgMeasurementCount = useMemo(() => {
  //   const orgs =
  //     authUser.selectedOrgs.length > 0
  //       ? authUser.orgs.filter((org) => authUser.selectedOrgs.includes(org.id))
  //       : authUser.orgs;
  //   return orgs.reduce((agg, org) => agg + (org.measurementCount || 0), 0);
  // }, [authUser.orgs, authUser.selectedOrgs]);

  const shouldShowNoTargetSystems =
    !targetSystems || targetSystems.length === 0;

  const screenShot = async () => {
    if (mainContentRef.current) {
      const clone = mainContentRef.current.cloneNode(true) as HTMLElement;
      const container = document.createElement('div');
      container.setAttribute(
        'style',
        `display: block; max-width: ${mainContentRef.current.scrollWidth}px; width: ${mainContentRef.current.scrollWidth}px; background-color: transparent`
      );
      container.setAttribute('class', 'mainContent');
      const elements = Array.from(
        clone.querySelectorAll('.customBarChartLabel')
      );
      elements.forEach((e: Element) =>
        e.classList.remove('customBarChartLabel')
      );

      container.appendChild(clone);
      document.body.appendChild(container);
      domToImage
        .toCanvas(container, { pixelRatio: 1 })
        .then((canvas: HTMLCanvasElement) => {
          canvas.toBlob((blob: any) => {
            if (blob) {
              saveAs(blob, 'output.png');
              container.remove();
            }
          }, 'image/png');
        });
    }
  };

  const statusCodes = filters
    .filter((f) => f.category === 'last_status_code')
    .map((f) => f.value);

  let targetSystemDevices: string[] = [];
  reports.forEach((rep) => {
    if (rep.targetSystemDevices) {
      Object.keys(rep.targetSystemDevices).forEach((val) => {
        targetSystemDevices.push(val);
      });
    }
  });
  const filteredTargetSystems = targetSystems.filter((ts) => {
    if (statusCodes && statusCodes.length > 0) {
      return (
        statusCodes.indexOf(
          ts.lastStatusCode ? ts.lastStatusCode.toString() : ''
        ) !== -1
      );
    }
    return filters.length ? targetSystemDevices.indexOf(ts.id) !== -1 : ts;
  });

  const csvExport = () => {
    let data = 'data:text/csv;base64,';
    const items = reportsInCSVFormat(reports);
    const encodedUri = encodeURI(data + btoa(items));
    var link = document.createElement('a');
    link.href = encodedUri;
    link.download = 'report.csv';
    link.click();
    link.remove();
  };

  const exportButton = () => (
    <Fragment key="export-button">
      <Button
        ref={exportMenuRef}
        size="small"
        disabled={isLoadingReports || isLoadingTs || !authUser.id}
        className={classes.screenshotButton}
        aria-haspopup="true"
        onClick={() => setIsExportMenuOpened(true)}
      >
        <span className="labelContainer">
          <span className="label">Export</span>
          <KeyboardArrowDown />
        </span>
      </Button>

      <Menu
        anchorEl={exportMenuRef.current}
        open={isExportMenuOpened}
        onClose={() => setIsExportMenuOpened(false)}
      >
        <MenuItem onClick={screenShot}>
          <Typography variant="body2">Export image</Typography>
        </MenuItem>
        <MenuItem onClick={csvExport}>
          <Typography variant="body2">Export CSV</Typography>
        </MenuItem>
      </Menu>
    </Fragment>
  );

  return (
    <div className={classes.dashboard}>
      <Layout
        isLoading={isLoadingReports || isLoadingTs || !authUser.id}
        error={error}
      >
        <Filters
          filters={filters}
          targetSystems={targetSystems}
          deviceCount={devicesCount}
          measurementCount={measurementCount}
          additionalFields={[exportButton()]}
          dashboard={true}
          sortOptionId={undefined}
        />
        <Grid container spacing={2} className="content" ref={mainContentRef}>
          <Grid item xs={12}>
            <Paper className={classes.statsStrip}>
              <div
                className="statItem clickable"
                onClick={() => history.push('/devices' + location.search)}
              >
                <Typography variant="h4" color="textSecondary" align="center">
                  <CountUp
                    start={0}
                    end={devicesCount}
                    redraw={true}
                    formattingFn={(value) => value.toLocaleString()}
                  />
                </Typography>
                <Typography variant="subtitle1" align="center">
                  Device{devicesCount === 1 ? '' : 's'}
                </Typography>
              </div>
              <Divider
                orientation={isMobile ? 'horizontal' : 'vertical'}
                flexItem
              />
              <div className="statItem">
                <Typography variant="h4" color="textSecondary" align="center">
                  <CountUp
                    start={0}
                    end={measurementCount}
                    redraw={true}
                    formattingFn={(value) => value.toLocaleString()}
                  />
                </Typography>
                <Typography variant="subtitle1" align="center">
                  Device Measurement{measurementCount === 1 ? '' : 's'}
                </Typography>
              </div>
              <Divider
                orientation={isMobile ? 'horizontal' : 'vertical'}
                flexItem
              />
              {/* <div className="statItem"> */}
              {/*   <Typography variant="h4" color="textSecondary" align="center"> */}
              {/*     <CountUp */}
              {/*       start={0} */}
              {/*       end={orgMeasurementCount} */}
              {/*       redraw={true} */}
              {/*       formattingFn={(value) => value.toLocaleString()} */}
              {/*     /> */}
              {/*   </Typography> */}
              {/*   <Typography variant="subtitle1" align="center"> */}
              {/*     Organization Measurement{orgMeasurementCount === 1 ? '' : 's'} */}
              {/*   </Typography> */}
              {/* </div> */}
            </Paper>
          </Grid>

          <Grid item xs={12} md={shouldShowNoTargetSystems ? 9 : 12}>
            <AlertsBarChart
              className={classes.statusPanel}
              knownIssues={knownIssues}
              reports={reports}
              disabledClick={isMaximumDevicesReached}
              targetSystems={filteredTargetSystems}
            />
          </Grid>
          {shouldShowNoTargetSystems ? (
            <Grid item xs={12} md={3}>
              <Paper className={classes.noTargetSystemsPanel}>
                <Typography variant="h2" color="textSecondary">
                  <DeviceHub className={classes.noTargetSystemIcon} />
                </Typography>
                No webhook found
              </Paper>
            </Grid>
          ) : null}

          {showBatteryVoltage && (
            <Grid item xs={12} md={mediumScreenWidth}>
              <BatteryHealthPieChart
                reports={reports}
                disabledClick={isMaximumDevicesReached}
              />
            </Grid>
          )}
          {showBatteryAvgVoltage && (
            <Grid item xs={12} md={mediumScreenWidth}>
              <BatteryHealthPieChart
                reports={reports}
                disabledClick={isMaximumDevicesReached}
                avg={true}
              />
            </Grid>
          )}

          {showSignalStrength ? (
            <Grid item xs={12} md={mediumScreenWidth}>
              <SignalStrengthPieChart
                reports={reports}
                disabledClick={isMaximumDevicesReached}
              />
            </Grid>
          ) : null}

          {showSignalAvgStrength ? (
            <Grid item xs={12} md={mediumScreenWidth}>
              <SignalStrengthPieChart
                reports={reports}
                disabledClick={isMaximumDevicesReached}
                avg={true}
              />
            </Grid>
          ) : null}

          {showStatusCodes ? (
            <>
              <Grid item xs={12} md={mediumScreenWidth}>
                <StatusCodePieChart
                  targetSystems={filteredTargetSystems}
                  type="target-system"
                  reports={reports}
                  disabledClick={isMaximumDevicesReached}
                />
              </Grid>
              <Grid item xs={12} md={mediumScreenWidth}>
                <StatusCodePieChart
                  targetSystems={filteredTargetSystems}
                  type="device"
                  reports={reports}
                  disabledClick={isMaximumDevicesReached}
                />
              </Grid>
            </>
          ) : null}

          {showSyncData && (
            <Grid item xs={12} md={mediumScreenWidth}>
              <SyncDataPieChart
                reports={reports}
                disabledClick={isMaximumDevicesReached}
              />
            </Grid>
          )}
        </Grid>
      </Layout>
    </div>
  );
};

export default memo(Dashboard);
